import type {
  CreateClubRequest,
  CreateClubResponse,
  HGCreateClubReply
} from '@ps/web-shared-api-types/v0/SwHomeGames';
import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { of } from 'rxjs';
import { catchError } from 'rxjs/operators';

import useRequest from '@swf/core/hooks/useRequest';
import useSearchParams from '@swf/core/hooks/useSearchParams';
import type { Dispatch } from '@swf/core/store';
import { handleServerResponse, useHomeGamesWidget } from '@swf/poker';

import ENDPOINTS from '../../../../constants/endpoints';
import { HGOnboarding, ModalRoutes, UserClubRole } from '../../../../types/common';
import type { FormValues } from '../CreateClub';
import type { CreateClubSuccessParams } from '../components/CreateClubSuccess/CreateClubSuccess';
import { generatePassword } from '../utils/randomPassword';

interface State {
  error?: string | null;
  message?: string | null;
  data: HGCreateClubReply;
}

interface CreateResponse {
  body: CreateClubResponse;
}

const initialResponse: State = { error: null, message: null, data: {} };

const useCreateClub = () => {
  const [response, setResponse] = useState(initialResponse);
  const [loading, setLoading] = useState(false);
  const request = useRequest();
  const { modal } = useDispatch<Dispatch>();
  const { widgetId } = useSearchParams<{ widgetId: string }>();

  const { widgetDispatch, widgetSelect } = useHomeGamesWidget(widgetId);

  const clubs = useSelector(widgetSelect.clubs);
  const hasCreatedClubs = clubs.some((club) => club.roleForUser === UserClubRole.Manager);

  const handleSubmit = ({ clubName, invitationCode, isPrivate = true }: FormValues) => {
    setLoading(true);
    request({
      service: ENDPOINTS.club,
      http: true,
      method: 'POST',
      body: {
        createClub: {
          clubName,
          password: isPrivate ? invitationCode : generatePassword(24),
          open: !isPrivate,
          discoverable: !isPrivate
        } as CreateClubRequest
      }
    })
      .pipe(catchError((err) => of(err)))
      .subscribe({
        next: ({ body }: CreateResponse) => {
          const { error, data, message } = handleServerResponse<
            CreateClubResponse,
            HGCreateClubReply
          >({
            response: body,
            accessor: 'createClubReply'
          });

          setResponse({ error, data, message });
          setLoading(false);

          if (!error) {
            if (!hasCreatedClubs) {
              widgetDispatch.setLocalStorageItem({
                onboarding: {
                  [HGOnboarding.CreatedClub]: true,
                  [HGOnboarding.NewClubCreateGame]: false
                }
              });
            }

            const locationState: CreateClubSuccessParams = {
              clubId: data?.clubId || 0,
              clubName,
              invitationCode,
              widgetId
            };
            modal.replace([{ path: ModalRoutes.createClubSuccess, locationState }]);
          }
        }
      });
  };

  return { response, loading, handleSubmit };
};

export default useCreateClub;
